import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {string} */
  #mobileDrawerQuery = '(max-width: 1199px)';

  /** @type {() => void} */
  #boundSyncMobileDrawerOffset = () => this.#syncMobileDrawerOffset();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#ensureClosedState();
    this.#bindSummaryClick();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#unbindSummaryClick();
    this.#stopMobileDrawerOffsetSync();
  }

  #bindSummaryClick() {
    const summary = this.refs.details.querySelector(':scope > summary');
    summary?.addEventListener('click', this.#onSummaryClick, { capture: true });
  }

  #unbindSummaryClick() {
    const summary = this.refs.details.querySelector(':scope > summary');
    summary?.removeEventListener('click', this.#onSummaryClick, { capture: true });
  }

  #onSummaryClick = (event) => {
    event.preventDefault();
    this.toggle(event);
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.classList.contains('menu-open');
  }

  #ensureClosedState() {
    const details = this.refs.details;
    const summary = details.querySelector('summary');

    details.classList.remove('menu-open');
    details.removeAttribute('open');
    summary?.setAttribute('aria-expanded', 'false');
  }

  /**
   * @param {Event} [event]
   */
  toggle(event) {
    if (event instanceof Event) {
      event.preventDefault();
    }

    return this.isOpen ? this.close() : this.open(undefined, event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    if (event instanceof Event) {
      event.preventDefault();
    }

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary || this.isOpen) return;

    summary.setAttribute('aria-expanded', 'true');
    details.setAttribute('open', '');
    details.classList.add('menu-open');
    this.#syncMobileDrawerOffset();
    this.#startMobileDrawerOffsetSync();

    this.preventInitialAccordionAnimations(details);

    if (target) {
      this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
    }

    requestAnimationFrame(() => {
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    if (!this.isOpen) return;
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    details.removeAttribute('open');

    if (details === this.refs.details) {
      this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
      this.#stopMobileDrawerOffsetSync();
      removeTrapFocus();
    }

    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  #syncMobileDrawerOffset() {
    if (!window.matchMedia(this.#mobileDrawerQuery).matches) return;

    const headerRow = document.querySelector('#header-component.custom-header .header__row--top');
    const offset = Math.round(headerRow?.getBoundingClientRect().bottom ?? 0);
    const top = `${Math.max(offset, 0)}px`;

    this.refs.menuDrawer.style.setProperty('--mobile-drawer-top', top);

    const backdrop = this.refs.details.querySelector('.menu-drawer__backdrop');
    backdrop?.style.setProperty('--mobile-drawer-top', top);
  }

  #resetMobileDrawerOffset() {
    this.refs.menuDrawer.style.removeProperty('--mobile-drawer-top');
    this.refs.details.querySelector('.menu-drawer__backdrop')?.style.removeProperty('--mobile-drawer-top');
  }

  #startMobileDrawerOffsetSync() {
    window.addEventListener('scroll', this.#boundSyncMobileDrawerOffset, { passive: true });
    window.addEventListener('resize', this.#boundSyncMobileDrawerOffset, { passive: true });
  }

  #stopMobileDrawerOffsetSync() {
    window.removeEventListener('scroll', this.#boundSyncMobileDrawerOffset);
    window.removeEventListener('resize', this.#boundSyncMobileDrawerOffset);
    this.#resetMobileDrawerOffset();
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
